import { useEffect, useState } from 'react'
import {
  ShieldCheck, ShieldAlert, KeyRound, Fingerprint, Clock3, MonitorSmartphone,
  CheckCircle2, ChevronDown, LifeBuoy, Globe2,
} from 'lucide-react'
import { apiGet, adminMutate, usePermissions, Select } from './primitives.js'
import { SettingsCrumb } from './SettingsCrumb.js'
import { Row, Segmented } from './appearance-controls.js'
import { useSaveBar } from '../shell/editing.js'
import { Link } from '../router.js'

/**
 * /settings/security (LOT 5.5.2) — la politique de sécurité de Gestion,
 * gérable par un administrateur NORMAL.
 *
 * Ce qu'on peut RÉGLER (bornes sûres, Save Bar, security.write + step-up) :
 *   · méthodes MFA autorisées (clés d'accès / TOTP) — garde anti-enfermement
 *   · session STANDARD : inactivité (30 min → 24 h) et durée absolue
 *     (2 h → 48 h, toujours ≥ inactivité) — LOT 5.5.3
 *   · « Rester connecté » (autorisé + durée max 7/14/30 j)
 *   · validité de la confirmation forte (2/5/10/15 min)
 *
 * Ce qu'on ne peut PAS régler ici, volontairement :
 *   · l'enforcement général (LOT 6, procédure dédiée — PAS un toggle)
 *   · les écritures externes (mode sûr)
 *   · la cryptographie (lecture seule, repliée dans Détails techniques)
 */

interface SecurityData {
  policy: {
    remember_allowed: boolean
    remember_days: number
    stepup_minutes: number
    mfa_passkey_allowed: boolean
    mfa_totp_allowed: boolean
    session_idle_minutes: number
    session_absolute_minutes: number
  }
  choices: {
    remember_days: number[]; stepup_minutes: number[]
    session_idle_minutes: number[]; session_absolute_minutes: number[]
  }
  facts: {
    auth_enforcement: boolean
    global_write: boolean
    sessions_active: number
    sessions_remembered: number
    accounts_mfa: number
    accounts_total: number
  }
  technical: {
    password_hash: string
    session_model: string
    cookie: string
    csrf: string
    webauthn_rp_id: string
    public_origin: string
  }
}

type Policy = SecurityData['policy']
const samePolicy = (a: Policy, b: Policy) =>
  a.remember_allowed === b.remember_allowed && a.remember_days === b.remember_days
  && a.stepup_minutes === b.stepup_minutes
  && a.mfa_passkey_allowed === b.mfa_passkey_allowed && a.mfa_totp_allowed === b.mfa_totp_allowed
  && a.session_idle_minutes === b.session_idle_minutes
  && a.session_absolute_minutes === b.session_absolute_minutes

/** Minutes → durée humaine. Jamais de secondes à l'écran. */
const dureeHumaine = (m: number) => {
  if (m < 60) return `${m} minutes`
  const h = m / 60
  return h === 1 ? '1 heure' : `${h} heures`
}

export function SecurityPage() {
  const { permissions, authenticated } = usePermissions()
  const canWrite = authenticated && permissions.includes('security.write')

  const [data, setData] = useState<SecurityData | null>(null)
  const [draft, setDraft] = useState<Policy | null>(null)
  const [tech, setTech] = useState(false)
  const secure = window.isSecureContext

  const charger = () => {
    apiGet<SecurityData>('/api/v1/admin/security').then((d) => {
      setData(d)
      setDraft({ ...d.policy })
    }).catch(() => undefined)
  }
  useEffect(charger, [])

  const p = draft ?? data?.policy ?? null
  const dirty = !!data && !!draft && !samePolicy(draft, data.policy)
  const change = (patch: Partial<Policy>) => { if (canWrite && draft) setDraft({ ...draft, ...patch }) }

  // Incohérence expliquée HUMAINEMENT avant même le serveur (qui revalide).
  const incoherent = !!p && p.session_absolute_minutes < p.session_idle_minutes
  useSaveBar({
    dirty: canWrite && dirty,
    invalid: incoherent
      ? 'La durée maximale d’une session doit être au moins égale à la fenêtre d’inactivité.'
      : null,
    successMessage: 'Politique de sécurité enregistrée.',
    onSave: async () => {
      if (!draft) return 'Chargement incomplet.'
      const r = await adminMutate<{ error?: string; message?: string }>('PUT', '/api/v1/admin/security/policy', draft)
      if (r.status >= 300) return r.data.message ?? 'La politique n’a pas été modifiée.'
      charger()
      return true
    },
    onCancel: () => { if (data) setDraft({ ...data.policy }) },
  })

  const fait = (label: string, value: React.ReactNode, hint?: string) => (
    <div className="ad-fact">
      <span className="ad-fact__label lc-stack">
        <span>{label}</span>
        {hint ? <span className="lc-hint">{hint}</span> : null}
      </span>
      <span className="ad-fact__value">{value}</span>
    </div>
  )

  return (
    <main className="lc-page lc-stagger">
      <SettingsCrumb />
      <h1 className="lc-title">Sécurité</h1>
      <p className="lc-subtitle">
        La politique de sécurité de Gestion, expliquée simplement — les
        réglages critiques restent hors de portée d’un simple clic.
      </p>

      {!canWrite ? (
        <div className="adm-note" style={{ maxWidth: 680 }}>
          Lecture seule : modifier la politique demande la permission dédiée
          (opération sensible, confirmée par une ré-authentification forte).
        </div>
      ) : null}

      {/* ═══ État de sécurité ═══ */}
      <section className="lc-panel gd-panel" style={{ marginTop: 'var(--sp-4)' }} aria-label="État de sécurité">
        <header className="lc-panel__head">
          <h2 className="lc-section-title"><ShieldCheck size={16} style={{ verticalAlign: -2, marginRight: 8 }} />État de sécurité</h2>
        </header>
        <div className="lc-panel__body lc-panel__body--flush ad-facts">
          {fait('Authentification générale',
            data ? (data.facts.auth_enforcement
              ? <span className="lc-pill lc-pill--ok">Imposée</span>
              : <span className="lc-pill lc-pill--warning"><ShieldAlert size={13} />Non encore imposée</span>) : '…',
            'Son activation suivra une procédure de recette dédiée — ce n’est volontairement pas un interrupteur.')}
          {fait('Écritures vers les systèmes externes',
            data ? (data.facts.global_write
              ? <span className="lc-pill lc-pill--error">ACTIVÉES</span>
              : <span className="lc-pill lc-pill--ok"><CheckCircle2 size={13} />Désactivées — mode sûr</span>) : '…')}
          {fait('Double authentification',
            data ? `${data.facts.accounts_mfa} compte${data.facts.accounts_mfa > 1 ? 's' : ''} équipé${data.facts.accounts_mfa > 1 ? 's' : ''} sur ${data.facts.accounts_total}` : '…',
            'Requise par défaut pour tout nouveau compte.')}
          {fait('Clés d’accès (passkeys)',
            secure
              ? <span className="lc-pill lc-pill--ok">Disponibles</span>
              : <span className="lc-pill lc-pill--idle">Disponibles via HTTPS</span>,
            secure ? undefined : 'Ce navigateur est connecté sans HTTPS — ouvrez Gestion via https://gestion.liveclubrennes.com pour les utiliser.')}
          {fait('SSO entreprise', <span className="lc-pill lc-pill--idle">Non configuré</span>)}
        </div>
      </section>

      {/* ═══ Double authentification ═══ */}
      <section className="lc-panel gd-panel" style={{ marginTop: 'var(--sp-5)' }} aria-label="Double authentification">
        <header className="lc-panel__head">
          <h2 className="lc-section-title"><Fingerprint size={16} style={{ verticalAlign: -2, marginRight: 8 }} />Double authentification</h2>
        </header>
        <div className="lc-panel__body ap-body">
          <div className="adm-note" style={{ marginTop: 0 }}>
            Les méthodes désactivées ne peuvent plus être ajoutées sur un
            compte. Personne n’est enfermé dehors : un compte qui ne possède
            QUE la méthode désactivée peut toujours l’utiliser, et Gestion
            refuse tout réglage qui priverait un Super Admin de sa dernière
            méthode.
          </div>
          <Row label="Clés d’accès (passkeys)" hint="Touch ID, Windows Hello, YubiKey, iPhone…">
            <Segmented ariaLabel="Clés d'accès autorisées" value={p?.mfa_passkey_allowed ? 'yes' : 'no'} disabled={!canWrite}
              onChange={(v) => change({ mfa_passkey_allowed: v === 'yes' })}
              options={[{ value: 'yes', label: 'Autorisées' }, { value: 'no', label: 'Désactivées' }]} />
          </Row>
          <Row label="Application d’authentification (TOTP)" hint="Code à 6 chiffres.">
            <Segmented ariaLabel="TOTP autorisé" value={p?.mfa_totp_allowed ? 'yes' : 'no'} disabled={!canWrite}
              onChange={(v) => change({ mfa_totp_allowed: v === 'yes' })}
              options={[{ value: 'yes', label: 'Autorisée' }, { value: 'no', label: 'Désactivée' }]} />
          </Row>
          <Row label="Codes de secours" hint="10 codes à usage unique, régénérables — toujours disponibles en dernier recours.">
            <span className="lc-pill lc-pill--idle">Toujours actifs</span>
          </Row>
          <Row label="Méthode préférée" hint="Chaque personne choisit la sienne dans son profil — la politique définit seulement ce qui est autorisé.">
            <span className="lc-pill lc-pill--idle">Choix personnel</span>
          </Row>
        </div>
      </section>

      {/* ═══ Sessions ═══ */}
      <section className="lc-panel gd-panel" style={{ marginTop: 'var(--sp-5)' }} aria-label="Sessions">
        <header className="lc-panel__head">
          <h2 className="lc-section-title"><MonitorSmartphone size={16} style={{ verticalAlign: -2, marginRight: 8 }} />Sessions</h2>
        </header>
        <div className="lc-panel__body ap-body">
          <Row label="Déconnecter après inactivité"
            hint="Session standard : sans activité pendant cette durée, il faut se reconnecter.">
            {canWrite ? (
              <Select compact value={String(p?.session_idle_minutes ?? 480)}
                onChange={(v) => change({ session_idle_minutes: Number(v) })}
                options={(data?.choices.session_idle_minutes ?? [480]).map((m) => ({ value: String(m), label: dureeHumaine(m) }))} />
            ) : <span className="lc-pill lc-pill--idle">{dureeHumaine(p?.session_idle_minutes ?? 480)}</span>}
          </Row>
          <Row label="Durée maximale d’une session"
            hint="Même utilisée sans interruption, une session standard se ferme à cette échéance.">
            {canWrite ? (
              <Select compact value={String(p?.session_absolute_minutes ?? 720)}
                onChange={(v) => change({ session_absolute_minutes: Number(v) })}
                options={(data?.choices.session_absolute_minutes ?? [720]).map((m) => ({ value: String(m), label: dureeHumaine(m) }))} />
            ) : <span className="lc-pill lc-pill--idle">{dureeHumaine(p?.session_absolute_minutes ?? 720)}</span>}
          </Row>
          {incoherent ? (
            <div className="adm-note" role="alert">
              Ces deux durées sont incohérentes : une session expirerait de sa
              durée maximale ({dureeHumaine(p!.session_absolute_minutes)}) avant
              même la fin de sa fenêtre d’inactivité ({dureeHumaine(p!.session_idle_minutes)}).
              Augmentez la durée maximale ou réduisez l’inactivité.
            </div>
          ) : null}
          <Row label="« Rester connecté »" hint="Propose une session persistante au moment de la connexion.">
            <Segmented ariaLabel="Rester connecté autorisé" value={p?.remember_allowed ? 'yes' : 'no'} disabled={!canWrite}
              onChange={(v) => change({ remember_allowed: v === 'yes' })}
              options={[{ value: 'yes', label: 'Autorisé' }, { value: 'no', label: 'Désactivé' }]} />
          </Row>
          {p?.remember_allowed ? (
            <Row label="Durée maximale" hint="Une session persistante meurt de toute façon à cette échéance.">
              <Segmented ariaLabel="Durée maximale de session persistante" value={String(p.remember_days)} disabled={!canWrite}
                onChange={(v) => change({ remember_days: Number(v) })}
                options={(data?.choices.remember_days ?? [7, 14, 30]).map((d) => ({ value: String(d), label: `${d} jours` }))} />
            </Row>
          ) : null}
          {fait('Sessions actives en ce moment',
            data ? `${data.facts.sessions_active} au total · ${data.facts.sessions_remembered} persistante${data.facts.sessions_remembered > 1 ? 's' : ''}` : '…')}
          <Row label="Vos propres sessions">
            <Link className="lc-btn" href="/profile#sessions">Voir mes sessions</Link>
          </Row>
        </div>
      </section>

      {/* ═══ Confirmation forte ═══ */}
      <section className="lc-panel gd-panel" style={{ marginTop: 'var(--sp-5)' }} aria-label="Confirmation forte">
        <header className="lc-panel__head">
          <h2 className="lc-section-title"><Clock3 size={16} style={{ verticalAlign: -2, marginRight: 8 }} />Confirmation forte (step-up)</h2>
        </header>
        <div className="lc-panel__body ap-body">
          <div className="adm-note" style={{ marginTop: 0 }}>
            Les actions sensibles exigent une confirmation forte récente (code
            ou clé d’accès) — même sur une session « Rester connecté » : une
            session peut durer 30 jours, la confiance pour les actions
            sensibles reste courte.
          </div>
          <Row label="Validité de la confirmation">
            <Segmented ariaLabel="Validité du step-up" value={String(p?.stepup_minutes ?? 5)} disabled={!canWrite}
              onChange={(v) => change({ stepup_minutes: Number(v) })}
              options={(data?.choices.stepup_minutes ?? [2, 5, 10, 15]).map((m) => ({ value: String(m), label: `${m} min` }))} />
          </Row>
          <Row label="Actions protégées" hint="Liste informative — appliquée par le serveur.">
            <span style={{ fontSize: 'var(--fs-13)', color: 'var(--text-2)', textAlign: 'right', maxWidth: 420 }}>
              Rôles et permissions · sécurité des comptes · changement d’e-mail ·
              gestion MFA · branding et réglages sensibles · secrets des services ·
              politique de sécurité
            </span>
          </Row>
        </div>
      </section>

      {/* ═══ Protection des connexions ═══ */}
      <section className="lc-panel gd-panel" style={{ marginTop: 'var(--sp-5)' }} aria-label="Protection des connexions">
        <header className="lc-panel__head">
          <h2 className="lc-section-title"><KeyRound size={16} style={{ verticalAlign: -2, marginRight: 8 }} />Protection des connexions</h2>
        </header>
        <div className="lc-panel__body lc-panel__body--flush ad-facts">
          {fait('Tentatives répétées', 'Verrouillage progressif après 5 échecs — 1 min, puis 2, 4, 8, plafonné à 15 min.')}
          {fait('Limitation de débit', '10 tentatives par minute et par adresse sur les routes de connexion.')}
          {fait('Messages d’erreur', 'Volontairement génériques — le formulaire ne révèle jamais si un e-mail existe.')}
        </div>
      </section>

      {/* ═══ Fournisseurs de connexion ═══ */}
      <section className="lc-panel gd-panel" style={{ marginTop: 'var(--sp-5)' }} aria-label="Fournisseurs de connexion">
        <header className="lc-panel__head">
          <h2 className="lc-section-title"><Globe2 size={16} style={{ verticalAlign: -2, marginRight: 8 }} />Fournisseurs de connexion</h2>
        </header>
        <div className="lc-panel__body lc-panel__body--flush ad-facts">
          {fait('Compte Gestion', <span className="lc-pill lc-pill--ok"><CheckCircle2 size={13} />Actif</span>,
            'Mot de passe + double authentification (TOTP ou clé d’accès).')}
          {fait('Google', <span className="lc-pill lc-pill--idle">Prévu</span>,
            'Arrivera avec le futur fournisseur d’identité commun (connexion unique pour Gestion, Main Courante et Parc).')}
          {fait('Apple', <span className="lc-pill lc-pill--idle">Prévu</span>,
            'Même chemin : fédéré par le futur fournisseur d’identité, pas branché en direct.')}
          {fait('SSO entreprise', <span className="lc-pill lc-pill--idle">Non configuré</span>,
            'La solution sera choisie dans un lot dédié.')}
        </div>
      </section>

      {/* ═══ Incident & récupération ═══ */}
      <section className="lc-panel gd-panel" style={{ marginTop: 'var(--sp-5)' }} aria-label="Incident et récupération">
        <header className="lc-panel__head">
          <h2 className="lc-section-title"><LifeBuoy size={16} style={{ verticalAlign: -2, marginRight: 8 }} />Incident & récupération</h2>
        </header>
        <div className="lc-panel__body lc-panel__body--flush ad-facts">
          {fait('Procédure break-glass', 'Documentée dans les runbooks du dépôt — hors navigateur, volontairement.')}
          {fait('Dernier Super Admin', <span className="lc-pill lc-pill--ok">Protégé</span>,
            'Aucune opération ne peut laisser Gestion sans Super Admin actif.')}
          {fait('Codes de secours', 'Disponibles pour chaque compte — jamais affichés une seconde fois.')}
        </div>
      </section>

      {/* ═══ Détails techniques (repliés) ═══ */}
      <button
        className="lc-btn lc-btn--ghost ad-tech-toggle"
        style={{ marginTop: 'var(--sp-5)', fontSize: 'var(--fs-12)' }}
        onClick={() => setTech(!tech)}
        aria-expanded={tech}
      >
        <ChevronDown size={14} style={{ transform: tech ? 'rotate(180deg)' : undefined, transition: 'transform .15s ease' }} />
        Détails techniques
      </button>
      {tech && data ? (
        <section className="lc-panel gd-panel" style={{ marginTop: 'var(--sp-3)' }} aria-label="Détails techniques">
          <div className="lc-panel__body lc-panel__body--flush ad-facts">
            {fait('Hachage des mots de passe', <span className="ad-mono">{data.technical.password_hash}</span>)}
            {fait('Modèle de session', <span style={{ fontSize: 'var(--fs-13)', color: 'var(--text-2)' }}>{data.technical.session_model}</span>)}
            {fait('Cookie', <span className="ad-mono" style={{ fontSize: 'var(--fs-12)' }}>{data.technical.cookie}</span>)}
            {fait('Anti-CSRF', <span style={{ fontSize: 'var(--fs-13)', color: 'var(--text-2)' }}>{data.technical.csrf}</span>)}
            {fait('WebAuthn RP ID', <span className="ad-mono">{data.technical.webauthn_rp_id}</span>)}
            {fait('Origine publique', <span className="ad-mono">{data.technical.public_origin}</span>)}
          </div>
        </section>
      ) : null}
    </main>
  )
}
